'use client'

import { Button } from '@/components/atoms/button'
import { Input } from '@/components/atoms/input'
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/organisms/pagination'
import { useTailwindMediaQuery } from '@/hooks/use-tailwind-media-query'
import { cn } from '@/lib/utils'
import { createURL } from '@/utils/create-url-blog'
import { formatToSlug } from '@/utils/format-to-slug'
import { Label } from '@radix-ui/react-label'
import { Search } from 'lucide-react'
import { PostCard } from '../post-card'
import { SelectOrder } from '../select-order'

interface Post {
  sys: { id: string; updatedAt: string }
  fields: {
    title: string
    slug: string
    cover: { fields: { file: { url: string; description: string } } }
    categories: { fields: { name: string } }[]
  }
}

interface PostsProps {
  initialPosts: Post[]
  totalPosts: number
  postsPerPage: number
  categories: any[]
  currentPage: number
  searchTerm: string
  selectedCategory: string
  sort: string
}

export function Posts({
  initialPosts,
  totalPosts,
  postsPerPage,
  categories,
  currentPage,
  searchTerm,
  selectedCategory,
  sort,
}: PostsProps) {
  const totalPages = Math.ceil(totalPosts / postsPerPage)
  const { isMd } = useTailwindMediaQuery()

  return (
    <>
      <section className="pt-4">
        <div className="flex flex-col sm:flex-row gap-8">
          <h1
            className={cn(
              'w-full text-display-md lg:text-display-sm font-semibold',
            )}
          >
            Olá, empreendedor!
          </h1>
          <div className="flex flex-col sm:flex-row w-full gap-4 items-center justify-end">
            <form
              action={createURL('search', '', {
                searchTerm,
                selectedCategory,
                sort,
                currentPage,
              })}
              method="GET"
              className="flex w-full items-center gap-2.5"
            >
              <Label htmlFor="search" className="hidden">
                Buscar Publicação
              </Label>
              <Input
                type="search"
                id="search"
                name="search"
                placeholder="Buscar"
                defaultValue={searchTerm}
              />
              <Button type="submit" className="rounded-lg">
                <Search size={16} />
              </Button>
            </form>

            <SelectOrder
              sort={sort}
              currentPage={currentPage}
              searchTerm={searchTerm}
              selectedCategory={selectedCategory}
            />
          </div>
        </div>

        <div className="flex flex-wrap gap-2 pt-8">
          <a
            href={createURL('categories', '', {
              searchTerm,
              selectedCategory: '',
              sort,
              currentPage,
            })}
            className={cn('rounded-full px-3 py-2', {
              'bg-[#01634f] text-white': !selectedCategory,
              border: selectedCategory,
            })}
          >
            Todos
          </a>

          {categories.map((category) => {
            const nameCategory = formatToSlug(category.fields.name!.toString())
            return (
              <a
                key={category.sys.id}
                href={createURL('categories', nameCategory, {
                  searchTerm,
                  selectedCategory: category.sys.id,
                  sort,
                  currentPage,
                })}
                className={cn('rounded-full flex items-center px-3 py-2', {
                  'bg-[#01634f] text-white': selectedCategory === nameCategory,
                  border: selectedCategory !== category.sys.id,
                })}
              >
                {category.fields.name}
              </a>
            )
          })}
        </div>
      </section>
      <div id="posts-grid" className="grid grid-cols-6 gap-6 pt-14">
        {initialPosts.map((post: any, index) => (
          <PostCard
            key={post.sys.id}
            srcImg={'https:' + post.fields.cover.fields.file.url}
            altImg={post.fields.cover.fields.file.description}
            slug={post.fields.slug}
            badges={post.fields.categories.map((c: any) => c.fields.name)}
            title={post.fields.title}
            publishDate={new Date(post.fields?.publishedAt)}
            index={index}
          />
        ))}
      </div>

      {totalPages > 1 && !isMd && (
        <Pagination className="py-8">
          <PaginationContent>
            {currentPage > 1 && (
              <PaginationPrevious
                href={createURL('page', currentPage - 1, {
                  searchTerm,
                  selectedCategory,
                  sort,
                  currentPage,
                })}
              >
                {'<'}
              </PaginationPrevious>
            )}
            <PaginationItem>
              <PaginationLink isActive={true}>{currentPage}</PaginationLink>
            </PaginationItem>
            {currentPage < totalPages && (
              <PaginationItem>
                <PaginationLink
                  href={createURL('page', currentPage + 1, {
                    searchTerm,
                    selectedCategory,
                    sort,
                    currentPage,
                  })}
                >
                  {currentPage + 1}
                </PaginationLink>
              </PaginationItem>
            )}
            {currentPage < totalPages && (
              <PaginationNext
                href={createURL('page', currentPage + 1, {
                  searchTerm,
                  selectedCategory,
                  sort,
                  currentPage,
                })}
              >
                {'>'}
              </PaginationNext>
            )}
          </PaginationContent>
        </Pagination>
      )}

      {totalPages > 1 && isMd && (
        <Pagination className="py-8">
          <PaginationContent>
            {currentPage > 1 && (
              <PaginationPrevious
                href={createURL('page', currentPage - 1, {
                  searchTerm,
                  selectedCategory,
                  sort,
                  currentPage,
                })}
              />
            )}
            {[...Array(totalPages)].map((_, index) => (
              <PaginationItem key={index}>
                <PaginationLink
                  isActive={index + 1 === currentPage}
                  href={createURL('page', index + 1, {
                    searchTerm,
                    selectedCategory,
                    sort,
                    currentPage,
                  })}
                >
                  {index + 1}
                </PaginationLink>
              </PaginationItem>
            ))}
            {currentPage < totalPages && (
              <PaginationNext
                href={createURL('page', currentPage + 1, {
                  searchTerm,
                  selectedCategory,
                  sort,
                  currentPage,
                })}
              />
            )}
          </PaginationContent>
        </Pagination>
      )}
    </>
  )
}
